/* eslint-disable quotes */
import { createTheme, ThemeOptions } from "@mui/material";

import { COLORS, SHADOWS, TYPOGRAPHY } from "./design-constants";
import { COLOR_TOKENS } from "./mui-colors";
import {
  MUI_BUTTON_CONFIG,
  MUI_CARD_CONFIG,
  MUI_CHIP_CONFIG,
  MUI_ICON_BUTTON_CONFIG,
  MUI_INPUT_CONFIG,
  MUI_MENU_CONFIG,
  MUI_POPUP_CONFIG,
  MUI_TAB_CONFIG,
  MUI_TABLE_CELL_CONFIG,
  MUI_TABLE_CONFIG
} from "./mui-component-configs";

export const muiThemeOptions: ThemeOptions = {
  palette: {
    primary: {
      contrastText: "#fff",
      dark: "#093136",
      light: "#1D878E",
      main: COLOR_TOKENS.MAIN.primary
    },
    secondary: {
      contrastText: "#fff",
      dark: "#373E4A",
      light: "#6D7B93",
      main: COLOR_TOKENS.MAIN.secondary
    },
    text: {
      disabled: "#97A6BF",
      secondary: "#58667D",
      primary: "#252D39"
    },
    background: {
      default: "#F8F9FD",
      paper: COLOR_TOKENS.BACKGROUND.white
    },
    divider: COLOR_TOKENS.STROKE.divider,
    success: {
      contrastText: "#fff",
      dark: "#035D47",
      light: COLOR_TOKENS.TECHNICAL.successLight,
      main: COLOR_TOKENS.TECHNICAL.success
    },
    info: {
      contrastText: "#fff",
      dark: "#195396",
      light: COLOR_TOKENS.TECHNICAL.infoLight,
      main: COLOR_TOKENS.TECHNICAL.info
    },
    warning: {
      contrastText: "#fff",
      dark: "#684C00",
      light: COLOR_TOKENS.TECHNICAL.warningLight,
      main: COLOR_TOKENS.TECHNICAL.warning
    },
    error: {
      contrastText: "#fff",
      dark: "#942828",
      light: COLOR_TOKENS.TECHNICAL.errorLight,
      main: COLOR_TOKENS.TECHNICAL.error
    },
    action: {
      focus: "rgba(255,255,255,0.12)",
      disabledBackground: "rgba(255,255,255,0.12)",
      // disabled: "rgba(255,255,255,0.54)", // Color issue
      selected: "rgba(255,255,255,0.92)",
      hover: "rgba(255,255,255,0.9)"
      // active: "rgba(255,255,255,0.54)"
    },
    turquoise: COLORS.TURQUOISE,
    gold: COLORS.GOLD,
    green: COLORS.GREEN,
    red: COLORS.RED,
    orange: COLORS.ORANGE,
    blue: COLORS.BLUE,
    gray: COLORS.GRAY,
    neutral: COLORS.NEUTRAL,
    yellow: COLORS.YELLOW,
    pink: COLORS.PINK,
    purpure: COLORS.PURPLE
    // #fff: {
    //   25: "#FFFFFF",
    //   50: "#FFFFFF",
    //   100: "#FFFFFF",
    //   200: "#FFFFFF",
    //   300: "#FFFFFF",
    //   400: "#FFFFFF",
    //   500: "#FFFFFF",
    //   600: "#FFFFFF",
    //   700: "#FFFFFF",
    //   800: "#FFFFFF",
    //   900: "#FFFFFF"
    // },
    // black: {
    //   25: "#000000",
    //   50: "#000000",
    //   100: "#000000",
    //   200: "#000000",
    //   300: "#000000",
    //   400: "#000000",
    //   500: "#000000",
    //   600: "#000000",
    //   700: "#000000",
    //   800: "#000000",
    //   900: "#000000"
    // }
  },
  boxShadow: {
    xs: SHADOWS.XS,
    sm: SHADOWS.SM,
    md: SHADOWS.MD,
    lg: SHADOWS.LG,
    xl: SHADOWS.XL,
    "2xl": SHADOWS["2XL"],

    "top-xs": SHADOWS["top-xs"],
    "top-sm": SHADOWS["top-sm"],
    "top-md": SHADOWS["top-md"],
    "top-lg": SHADOWS["top-lg"],
    "top-xl": SHADOWS["top-xl"],
    "top-2xl": SHADOWS["top-2xl"],

    "right-xs": SHADOWS["right-xs"],
    "right-sm": SHADOWS["right-sm"],
    "right-md": SHADOWS["right-md"],
    "right-lg": SHADOWS["right-lg"],
    "right-xl": SHADOWS["right-xl"],
    "right-2xl": SHADOWS["right-2xl"]
  },
  unstable_sxConfig: {
    borderRadius: {
      themeKey: "shape"
    },
    boxShadow: {
      themeKey: "boxShadow"
    }
  },
  // shape: {
  //   xs: 2,
  //   sm: 4,
  //   md: 8,
  //   lg: 16,
  //   xl: 24,
  //   xxl: 32,
  //   xxxl: 40,
  //   "4xl": 64,
  //   "5xl": 128,
  //   round: "50%"
  // },
  spacing: 4,
  typography: {
    fontFamily: "Roboto, sans-serif",
    fontWeightBold: 700,
    fontWeightLight: 300,
    fontWeightMedium: 500,
    fontWeightRegular: 400,
    h2: TYPOGRAPHY.h2,
    h3: TYPOGRAPHY.h3,
    h4: TYPOGRAPHY.h4,
    h5: TYPOGRAPHY.h5,
    h6: TYPOGRAPHY.h6,
    button: TYPOGRAPHY.button,
    body1: TYPOGRAPHY.body1,
    body2: TYPOGRAPHY.body2,
    body3: TYPOGRAPHY.body3,
    body4: TYPOGRAPHY.body4
  },
  components: {
    MuiSvgIcon: {
      variants: [
        {
          props: { fontSize: "xs" },
          style: {
            fontSize: 8
          }
        },
        {
          props: { fontSize: "sm" },
          style: {
            fontSize: 16
          }
        },
        {
          props: { fontSize: "md" },
          style: {
            fontSize: 20
          }
        },
        {
          props: { fontSize: "lg" },
          style: {
            fontSize: 24
          }
        },
        {
          props: { fontSize: "xl" },
          style: {
            fontSize: 32
          }
        }
      ],
      defaultProps: {
        fontSize: "md"
      },
      styleOverrides: {}
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: "button", fontSize: "small" },
          style: {
            fontSize: "14px",
            lineHeight: "16px"
          }
        },
        {
          props: { variant: "button", fontSize: "medium" },
          style: {
            fontSize: "16px",
            lineHeight: "24px"
          }
        }
      ]
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": { fontSize: MUI_TAB_CONFIG.iconSize },
          "& .MuiTab-iconWrapper": { fontSize: MUI_TAB_CONFIG.iconSize }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: MUI_TABLE_CELL_CONFIG.body.color,
          fontSize: MUI_TABLE_CELL_CONFIG.body.fontSize,
          lineHeight: MUI_TABLE_CELL_CONFIG.body.lineHeight,
          fontWeight: MUI_TABLE_CELL_CONFIG.body.fontWeight,
          padding: `${MUI_TABLE_CELL_CONFIG.body.paddingVertical} ${MUI_TABLE_CELL_CONFIG.body.paddingHorizontal}`,
          borderBottom: `1px solid ${MUI_TABLE_CELL_CONFIG.body.borderColor}`,
          borderLeft: "none",
          borderRight: "none"
        },
        head: {
          color: MUI_TABLE_CELL_CONFIG.head.color,
          backgroundColor: MUI_TABLE_CELL_CONFIG.head.background,
          fontSize: MUI_TABLE_CELL_CONFIG.head.fontSize,
          lineHeight: MUI_TABLE_CELL_CONFIG.head.lineHeight,
          fontWeight: MUI_TABLE_CELL_CONFIG.head.fontWeight,
          padding: `${MUI_TABLE_CELL_CONFIG.head.paddingVertical} ${MUI_TABLE_CELL_CONFIG.head.paddingHorizontal}`,
          borderBottom: "none"
        },
        footer: {
          fontWeight: 600
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-child td": { borderBottom: "none" },
          "&:hover td": { backgroundColor: MUI_TABLE_CONFIG.rowHoverBackground }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: "rgba(0, 0, 0, 0.54)"
        }
      }
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: "#fff"
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          display: "flex",
          flexDirection: "column",
          borderRadius: MUI_POPUP_CONFIG.borderRadius,
          padding: MUI_POPUP_CONFIG.padding,
          gap: MUI_POPUP_CONFIG.gap
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          display: "block",
          padding: 0,
          gap: 0,
          borderRadius: MUI_MENU_CONFIG.borderRadius,
          "& .MuiMenuItem-root .MuiListItemIcon-root": {
            minWidth: MUI_MENU_CONFIG.iconSize
          },
          "& .MuiMenuItem-root .MuiSvgIcon-root": {
            fontSize: MUI_MENU_CONFIG.iconSize,
            width: MUI_MENU_CONFIG.iconSize,
            height: MUI_MENU_CONFIG.iconSize
          }
        }
      },
      variants: [
        {
          props: { size: "md" },
          style: {
            "& .MuiMenuItem-root": {
              padding: `${MUI_MENU_CONFIG.md.paddingVertical} ${MUI_MENU_CONFIG.md.paddingHorizontal}`,
              gap: MUI_MENU_CONFIG.md.gap
            }
          }
        },
        {
          props: { size: "lg" },
          style: {
            "& .MuiMenuItem-root": {
              padding: `${MUI_MENU_CONFIG.lg.paddingVertical} ${MUI_MENU_CONFIG.lg.paddingHorizontal}`,
              gap: MUI_MENU_CONFIG.lg.gap
            }
          }
        }
      ],
      defaultProps: {
        size: "md"
      }
    },
    // MuiFormControl: {
    //   defaultProps: {
    //     sx: { marginBottom: "0px" },
    //   },
    // },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          "& legend": {
            width: "auto"
          }
        },
        root: {
          borderRadius: MUI_INPUT_CONFIG.borderRadius,
          "& .MuiInputBase-root.Mui-disabled": {
            "& > fieldset": {
              borderColor: "inherit"
            }
          }
        }
      },
      variants: [
        {
          props: { size: "md" },
          style: {
            "& .MuiOutlinedInput-input": {
              padding: `${MUI_INPUT_CONFIG.md.paddingVertical} ${MUI_INPUT_CONFIG.md.paddingHorizontal}`
            }
          }
        },
        {
          props: { size: "lg" },
          style: {
            "& .MuiOutlinedInput-input": {
              padding: `${MUI_INPUT_CONFIG.lg.paddingVertical} ${MUI_INPUT_CONFIG.lg.paddingHorizontal}`
            }
          }
        },
        {
          props: { size: "xl" },
          style: {
            "& .MuiOutlinedInput-input": {
              padding: `${MUI_INPUT_CONFIG.xl.paddingVertical} ${MUI_INPUT_CONFIG.xl.paddingHorizontal}`
            }
          }
        }
      ],
      defaultProps: {
        notched: true
      }
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          // maxHeight: "100%",
          border: `1px solid ${MUI_TABLE_CONFIG.borderColor}`,
          borderRadius: MUI_TABLE_CONFIG.borderRadius,
          overflow: "hidden"
        }
      }
    },
    MuiButton: {
      variants: [
        {
          props: { size: "sm" },
          style: {
            fontSize: "12px",
            minHeight: 24,
            height: 24,
            padding: `${MUI_BUTTON_CONFIG.sm.paddingVertical} ${MUI_BUTTON_CONFIG.sm.paddingHorizontal}`,
            gap: MUI_BUTTON_CONFIG.sm.gap,
            span: {
              ".MuiSvgIcon-root": {
                fontSize: "16px"
              }
            }
          }
        },
        {
          props: { size: "md" },
          style: {
            fontSize: 16,
            minHeight: 32,
            height: 32,
            padding: `${MUI_BUTTON_CONFIG.md.paddingVertical} ${MUI_BUTTON_CONFIG.md.paddingHorizontal}`,
            gap: MUI_BUTTON_CONFIG.md.gap
          }
        },
        {
          props: { size: "lg" },
          style: {
            fontSize: 16,
            minHeight: 40,
            height: 40,
            padding: `${MUI_BUTTON_CONFIG.lg.paddingVertical} ${MUI_BUTTON_CONFIG.lg.paddingHorizontal}`,
            gap: MUI_BUTTON_CONFIG.lg.gap,
            span: {
              ".MuiSvgIcon-root": {
                fontSize: "24px"
              }
            }
          }
        },
        // primary (contained)
        {
          props: { variant: "contained" },
          style: {
            backgroundColor: MUI_BUTTON_CONFIG.variants.primary.background,
            color: MUI_BUTTON_CONFIG.variants.primary.color,
            "&:hover": {
              backgroundColor: MUI_BUTTON_CONFIG.variants.primary.background,
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                backgroundColor: MUI_BUTTON_CONFIG.variants.primary.hoverBackground
              }
            },
            "&:active": {
              backgroundColor: MUI_BUTTON_CONFIG.variants.primary.background,
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                backgroundColor: MUI_BUTTON_CONFIG.variants.primary.pressedBackground
              }
            },
            "&:disabled": {
              backgroundColor: MUI_BUTTON_CONFIG.variants.primary.background,
              color: MUI_BUTTON_CONFIG.variants.primary.color,
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                backgroundColor: MUI_BUTTON_CONFIG.variants.primary.disabledBackground
              }
            }
          }
        },
        // outlined
        {
          props: { variant: "outlined" },
          style: {
            backgroundColor: MUI_BUTTON_CONFIG.variants.outlined.background,
            color: MUI_BUTTON_CONFIG.variants.outlined.color,
            borderColor: MUI_BUTTON_CONFIG.variants.outlined.borderColor,
            // background-image (gradient) cannot be transitioned, so animating
            // background-color leaks the primary fallback during the un-hover phase.
            transition: "color 250ms, border-color 250ms, box-shadow 250ms",
            "&:hover": {
              borderColor: MUI_BUTTON_CONFIG.variants.outlined.borderColor,
              background: MUI_BUTTON_CONFIG.variants.outlined.hoverBackground
            },
            "&:active": {
              background: MUI_BUTTON_CONFIG.variants.outlined.pressedBackground
            },
            "&:disabled": {
              color: MUI_BUTTON_CONFIG.variants.outlined.disabledColor,
              borderColor: MUI_BUTTON_CONFIG.variants.outlined.disabledColor
            }
          }
        },
        // flat (custom variant)
        {
          props: { variant: "flat" },
          style: {
            backgroundColor: MUI_BUTTON_CONFIG.variants.flat.background,
            color: MUI_BUTTON_CONFIG.variants.flat.color,
            transition: "color 250ms, border-color 250ms, box-shadow 250ms",
            "&:hover": {
              background: MUI_BUTTON_CONFIG.variants.flat.hoverBackground
            },
            "&:active": {
              background: MUI_BUTTON_CONFIG.variants.flat.pressedBackground
            },
            "&:disabled": {
              color: MUI_BUTTON_CONFIG.variants.flat.disabledColor,
              background: "none"
            }
          }
        },
        // text
        {
          props: { variant: "text" },
          style: {
            color: MUI_BUTTON_CONFIG.variants.text.color,
            transition: "color 250ms, border-color 250ms, box-shadow 250ms",
            "&:hover": {
              background: MUI_BUTTON_CONFIG.variants.text.hoverBackground
            },
            "&:active": {
              background: MUI_BUTTON_CONFIG.variants.text.pressedBackground
            },
            "&:disabled": {
              color: MUI_BUTTON_CONFIG.variants.text.disabledColor
            }
          }
        }
      ],
      styleOverrides: {
        outlined: {
          padding: `${MUI_BUTTON_CONFIG.md.paddingVertical} ${MUI_BUTTON_CONFIG.md.paddingHorizontal}`
        },
        root: {
          position: "relative",
          overflow: "hidden",
          whiteSpace: "nowrap",
          borderRadius: MUI_BUTTON_CONFIG.borderRadius,
          minWidth: "65px",
          fontWeight: 400,
          span: {
            margin: 0
          }
        }
      },
      defaultProps: {
        size: "md"
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: MUI_ICON_BUTTON_CONFIG.padding,
          borderRadius: MUI_ICON_BUTTON_CONFIG.borderRadius
        }
      },
      variants: [
        // sizes
        {
          props: { size: "sm" },
          style: {
            width: MUI_ICON_BUTTON_CONFIG.sizes.sm.size,
            height: MUI_ICON_BUTTON_CONFIG.sizes.sm.size,
            ".MuiSvgIcon-root": {
              fontSize: MUI_ICON_BUTTON_CONFIG.sizes.sm.iconSize
            }
          }
        },
        {
          props: { size: "md" },
          style: {
            width: MUI_ICON_BUTTON_CONFIG.sizes.md.size,
            height: MUI_ICON_BUTTON_CONFIG.sizes.md.size,
            ".MuiSvgIcon-root": {
              fontSize: MUI_ICON_BUTTON_CONFIG.sizes.md.iconSize
            }
          }
        },
        {
          props: { size: "lg" },
          style: {
            width: MUI_ICON_BUTTON_CONFIG.sizes.lg.size,
            height: MUI_ICON_BUTTON_CONFIG.sizes.lg.size,
            ".MuiSvgIcon-root": {
              fontSize: MUI_ICON_BUTTON_CONFIG.sizes.lg.iconSize
            }
          }
        },
        // primary (filled)
        {
          props: { variant: "primary" },
          style: {
            backgroundColor: MUI_ICON_BUTTON_CONFIG.variants.primary.background,
            color: MUI_ICON_BUTTON_CONFIG.variants.primary.color,
            "&:hover": {
              backgroundColor: MUI_ICON_BUTTON_CONFIG.variants.primary.background,
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                backgroundColor: MUI_ICON_BUTTON_CONFIG.variants.primary.hoverBackground
              }
            },
            "&:active": {
              backgroundColor: MUI_ICON_BUTTON_CONFIG.variants.primary.background,
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                backgroundColor: MUI_ICON_BUTTON_CONFIG.variants.primary.pressedBackground
              }
            },
            "&:disabled": {
              backgroundColor: MUI_ICON_BUTTON_CONFIG.variants.primary.background,
              color: MUI_ICON_BUTTON_CONFIG.variants.primary.color,
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                backgroundColor: MUI_ICON_BUTTON_CONFIG.variants.primary.disabledBackground
              }
            }
          }
        },
        // outlined
        {
          props: { variant: "outlined" },
          style: {
            backgroundColor: MUI_ICON_BUTTON_CONFIG.variants.outlined.background,
            color: MUI_ICON_BUTTON_CONFIG.variants.outlined.color,
            border: `1px solid ${MUI_ICON_BUTTON_CONFIG.variants.outlined.borderColor}`,
            transition: "color 250ms, border-color 250ms, box-shadow 250ms",
            "&:hover": {
              borderColor: MUI_ICON_BUTTON_CONFIG.variants.outlined.borderColor,
              background: MUI_ICON_BUTTON_CONFIG.variants.outlined.hoverBackground
            },
            "&:active": {
              background: MUI_ICON_BUTTON_CONFIG.variants.outlined.pressedBackground
            },
            "&:disabled": {
              color: MUI_ICON_BUTTON_CONFIG.variants.outlined.disabledColor,
              borderColor: MUI_ICON_BUTTON_CONFIG.variants.outlined.disabledColor
            }
          }
        },
        // flat (custom variant)
        {
          props: { variant: "flat" },
          style: {
            backgroundColor: MUI_ICON_BUTTON_CONFIG.variants.flat.background,
            color: MUI_ICON_BUTTON_CONFIG.variants.flat.color,
            transition: "color 250ms, box-shadow 250ms",
            "&:hover": {
              background: MUI_ICON_BUTTON_CONFIG.variants.flat.hoverBackground
            },
            "&:active": {
              background: MUI_ICON_BUTTON_CONFIG.variants.flat.pressedBackground
            },
            "&:disabled": {
              color: MUI_ICON_BUTTON_CONFIG.variants.flat.disabledColor,
              background: MUI_ICON_BUTTON_CONFIG.variants.flat.disabledBackground
            }
          }
        },
        // text
        {
          props: { variant: "text" },
          style: {
            color: MUI_ICON_BUTTON_CONFIG.variants.text.color,
            transition: "color 250ms, box-shadow 250ms",
            "&:hover": {
              background: MUI_ICON_BUTTON_CONFIG.variants.text.hoverBackground
            },
            "&:active": {
              background: MUI_ICON_BUTTON_CONFIG.variants.text.pressedBackground
            },
            "&:disabled": {
              color: MUI_ICON_BUTTON_CONFIG.variants.text.disabledColor
            }
          }
        }
      ],
      defaultProps: {
        size: "md"
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: { color: "red" }
      }
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: 0
        }
      },
      defaultProps: {
        InputLabelProps: { shrink: true },
        variant: "outlined"
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        input: {
          cursor: "pointer"
        },
        option: {
          '&[aria-selected="true"]': {
            backgroundColor: "#edf1f9"
          }
        }
      },
      defaultProps: {
        // InputLabelProps: { shrink: true },
        size: "small"
        // fullWidth: true
      },
      variants: [
        {
          props: { size: "md" },
          style: {
            "& .MuiOutlinedInput-root": {
              padding: `${MUI_INPUT_CONFIG.md.paddingVertical} ${MUI_INPUT_CONFIG.md.paddingHorizontal}`
            },
            "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
              padding: 0
            }
          }
        },
        {
          props: { size: "lg" },
          style: {
            "& .MuiOutlinedInput-root": {
              padding: `${MUI_INPUT_CONFIG.lg.paddingVertical} ${MUI_INPUT_CONFIG.lg.paddingHorizontal}`
            },
            "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
              padding: 0
            }
          }
        },
        {
          props: { size: "xl" },
          style: {
            "& .MuiOutlinedInput-root": {
              padding: `${MUI_INPUT_CONFIG.xl.paddingVertical} ${MUI_INPUT_CONFIG.xl.paddingHorizontal}`
            },
            "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
              padding: 0
            }
          }
        }
      ]
    },
    MuiInputLabel: {
      defaultProps: { shrink: true }
    },
    // MuiGrid: {
    //   styleOverrides: {
    //     root: {
    //       marginTop: 0,
    //       marginLeft: 0,
    //       width: "auto"
    //     }
    //   }
    // }
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: "#5d7782",
          borderColor: "#5d7782"
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: "#eaedef"
        }
      },
      variants: [
        {
          props: { size: "small" },
          style: {
            padding: MUI_CHIP_CONFIG.sm.paddingAll,
            "& .MuiChip-label": {
              padding: MUI_CHIP_CONFIG.sm.paddingLabel
            }
          }
        }
      ]
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.26)"
          }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.26)"
          }
        }
      }
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        grouped: {
          color: "rgba(0, 0, 0, 0.54)"
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: MUI_CARD_CONFIG.borderRadius,
          backgroundColor: MUI_CARD_CONFIG.background,
          padding: MUI_CARD_CONFIG.paddingGap,
          gap: MUI_CARD_CONFIG.paddingGap
        }
      }
    }
  }
};

export const muiThemes = createTheme(muiThemeOptions);
